import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Alert, Button } from "react-bootstrap";
import { ActionHandlerFunction, AdminApprovalGroup, ChangeOrgApprovalGroupRequest, OrgInfo } from "@preveil-api";
import { Account, AdminApprovalGroupRole, AdminApprovalGroupRoleMapping, AdminApprovalGroupRoles, AdminUIActionTypes, OrganizationInformation, OrgUserRoles, PrivateRoutes } from "src/common";
import { InfoPopover, Loading } from "src/components";
import { AssignApprovalGroupModal } from "..";
import _ from "lodash";

type AllProps = {
  approval_groups: AdminApprovalGroup[];
  organization_info: OrganizationInformation;
  is_loading: boolean;
  handleAction: ActionHandlerFunction;
  current_account: Account;
  org_info: OrgInfo;
};

/* Description: Assign tab component where a user can assign an approval group specifically for admin management or data export */
function AssignTabComponent(props: AllProps) {
  const { approval_groups, organization_info, is_loading, handleAction, current_account, org_info } = props;
  const navigate = useNavigate();
  const [existing_group, setExistingGroup] = useState<AdminApprovalGroup | undefined>();
  const [show_modal, setShowModal] = useState<boolean>(false);
  const [role, setRole] = useState<string>("");

  /* Description: For a specific role, get the AdminApprovalGroupRoleMapping. */
  function getApprovalGroupRoleMapping(role: string): AdminApprovalGroupRole {
    return role === "admin_approval_group"
      ? AdminApprovalGroupRoleMapping.admin_approval_group
      : AdminApprovalGroupRoleMapping.export_approval_group;
  }

  // Description: Get the approval group for a specific activity
  function getActivityApprovalGroup(activity: string): AdminApprovalGroup | undefined {
    return approval_groups.find((a_g) => a_g.roles.includes(activity));
  }

  // Description: Get the number of users with a recovery group assigned
  function getUsersWithRecovery(): number {
    return organization_info.lightweight_org_users.filter((user) => !!user.recovery_group_id).length;
  }

  /* Description: Checks if the specific role (admin management or data export) has pending requests */
  function roleHasRequests(role: string): boolean {
    return (
      organization_info.org_requests.filter((request) => {
        if (request.type === "change_org_approval_group_role") {
          const parsed_payload_data = request.parsed_payload.data as ChangeOrgApprovalGroupRequest;
          return parsed_payload_data.group_role === role;
        } else {
          return false;
        }
      }).length > 0
    );
  }

  /* Description: Checks if there exists an approval group that has approvers that all have admin privileges */
  function adminOnlyGroupsExist(role: string): boolean {
    return (
      !!getApprovalGroupRoleMapping(role).all_admin &&
      approval_groups.filter((a) => {
        return (
          a.approvers.filter((approver) => approver.role === OrgUserRoles.admin).length ===
          a.approvers.length
        );
      }).length > 0
    );
  }

  // Description: Handles Click of Create/Assign button - checks if the role is admin only or if there are no approval groups
  function assignOnClick(role: string) {
    const admin_only = !!getApprovalGroupRoleMapping(role).all_admin && !adminOnlyGroupsExist(role);
    if (admin_only || approval_groups.length === 0) {
      handleAction({ actionType: AdminUIActionTypes.SetShow });
    } else {
      setRole(role);
      setExistingGroup(getActivityApprovalGroup(role));
      setShowModal(true);
    }
  }

  return (
    <>
      {!!is_loading && <Loading className="in-place mt-5"></Loading>}
      {!is_loading && (
        <div className="max-800">
          {!!show_modal && <AssignApprovalGroupModal
            setShow={setShowModal}
            role={getApprovalGroupRoleMapping(role)}
            approval_groups={approval_groups}
            handleAction={handleAction}
            existing_group={existing_group}
            current_account={current_account}
            org_info={org_info}
          ></AssignApprovalGroupModal>}
          <h3 className="text-muted">Account Recovery</h3>
          <Alert variant="dark" className="text-muted align-center mb-4">
            <span>
              <b>{getUsersWithRecovery()}</b> of <b>{organization_info.lightweight_org_users.length}</b> users
              have a recovery group assigned
            </span>
            <Button className="ms-auto" variant="outline-secondary" onClick={() => navigate(`/admin/${PrivateRoutes.users_route}`)}>
              Manage Users
            </Button>
          </Alert>
          <h3 className="text-muted">Roles</h3>
          <div className="table-display">
            <Row className="header-card-row d-none d-sm-flex">
              <Col sm={5}>Role</Col>
              <Col sm={3} className="ps-0">Approved By</Col>
              <Col></Col>
            </Row>
            <div className="table-body">
              {_.map(AdminApprovalGroupRoles, (r: string, i: number) => {
                return (
                  <Row key={i}>
                    <Col sm={5}>
                      {getApprovalGroupRoleMapping(r).name}
                      <InfoPopover message={getApprovalGroupRoleMapping(r).details} />
                    </Col>
                    <Col sm={3} className={!getActivityApprovalGroup(r) ? "error-red ps-0" : "ps-0"}>
                      {!!getActivityApprovalGroup(r)
                        ? getActivityApprovalGroup(r)?.name
                        : "Unassigned"}
                    </Col>
                    <Col sm="auto" className="ms-auto">
                      {!!roleHasRequests(r) && (
                        <Button
                          className="notification-count yellow me-3"
                          aria-label="warning"
                          data-tooltip-id="pv-tooltip"
                          data-tooltip-content="Pending Approvals to change group. Click to see approval request."
                          onClick={() => navigate(`/admin/${PrivateRoutes.requests_route}`)}>!</Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => assignOnClick(r)}
                        aria-label="Assign"
                      >
                        {(!!getApprovalGroupRoleMapping(r).all_admin && !adminOnlyGroupsExist(r)) ||
                          approval_groups.length === 0
                          ? "Create"
                          : "Assign"}
                      </Button>
                    </Col>
                  </Row>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default React.memo(AssignTabComponent);
